import React, { useCallback, useState } from "react";
import styled from "styled-components";
import strings from "localisation/strings";
import RadioButton from "uiComponents/radioButton/RadioButton";
import { Label } from "components/forms/FormFields/InlineInputGroup";
import useQuery from "common/hooks/useQuery";
import { getActiveCities } from "api/resources";
import { City } from "api/models/City";
import CustomModal from "components/modal/CustomModal";
import { registerUserCity } from "api/users";
import useMutation from "common/hooks/useMutation";
import loginState from "state/singletons/loginState";
import notificationState from "state/singletons/notificationState";
import useLoginState from "hooks/useLoginState";
import { tryRefreshingWithLoginTokens } from "api/AuthedAPI";

type CitySelectionProps = {
  selectedCity?: City;
  cities?: City[];
  onCitySelection: (city: City) => void;
};

const RadioButtonContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  flex: 1;
`;

const CitySelection = ({
  selectedCity,
  cities,
  onCitySelection,
}: CitySelectionProps) => {
  return (
    <RadioButtonContainer>
      <Label isBold>{strings("wizardScreen.citySelection.title")}</Label>
      {cities?.map((city, index) => (
        <RadioButton
          key={index}
          onChange={() => onCitySelection(city)}
          checked={city === selectedCity}
          label={city.name}
          value={city.name}
        />
      ))}
    </RadioButtonContainer>
  );
};

const CitySelectionModal = () => {
  const { getCity, setCity } = useLoginState();
  const { refreshToken } = loginState.getState();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(
    getCity() ? false : true,
  );
  const [selectedCity, setSelectedCity] = useState<City | undefined>();
  const { result: activeCities = [] } = useQuery<City[]>({
    request: getActiveCities,
  });
  const { makeRequest: registerUserCityRequest } = useMutation(
    registerUserCity,
  );

  const handleCitySelection = useCallback((city: City) => {
    setSelectedCity(city);
  }, []);

  const onConfirmButtonClick = async () => {
    const response = await registerUserCityRequest({
      city: selectedCity?.name.toUpperCase() ?? "WUPPERTAL",
      refreshToken: refreshToken,
    });

    if (response && response.error) {
      if (response.error && response.error.detail) {
        notificationState.addErrorNotification(response.error.detail);
      }
    } else if (response && response.result && !response.error) {
      tryRefreshingWithLoginTokens(response.result);
      setCity(selectedCity?.name.toUpperCase() ?? "WUPPERTAL");
      setIsModalVisible(false);
    }
  };

  return (
    <>
      <CustomModal
        modalVisible={isModalVisible}
        onConfirm={onConfirmButtonClick}
        blurred
        isConfirmButtonDisabled={selectedCity === undefined}
      >
        <CitySelection
          selectedCity={selectedCity}
          cities={activeCities}
          onCitySelection={handleCitySelection}
        />
      </CustomModal>
    </>
  );
};

export default CitySelectionModal;
